import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { formatDate } from '../lib/format';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Save, Briefcase, Plus } from 'lucide-react';

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/clients/${clientId}`).then(r => {
      setClient(r.data);
      setForm(r.data);
    }).catch(() => toast.error('Client not found'));
    api.get(`/projects?client_id=${clientId}`).then(r => setProjects(r.data));
  }, [clientId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/clients/${clientId}`, {
        name: form.name, contact_person: form.contact_person, email: form.email,
        phone: form.phone, address: form.address, city: form.city,
        province: form.province, postal_code: form.postal_code, notes: form.notes,
      });
      setClient(res.data);
      setEditing(false);
      toast.success('Client updated');
    } catch (err) {
      toast.error('Failed to update client');
    } finally {
      setSaving(false);
    }
  };

  if (!client) return <div className="p-8 text-slate-500">Loading...</div>;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => navigate('/clients')}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{client.name}</h1>
          <p className="text-sm text-slate-500">Client since {formatDate(client.created_at)}</p>
        </div>
        {!editing ? (
          <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-client-button">Edit</Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setEditing(false); setForm(client); }}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSave} disabled={saving} data-testid="save-client-button">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border border-slate-200 shadow-sm">
          <CardHeader><CardTitle className="text-lg font-medium">Client Information</CardTitle></CardHeader>
          <CardContent>
            {editing ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Name</Label><Input name="name" value={form.name || ''} onChange={handleChange} /></div>
                <div className="space-y-2"><Label>Contact Person</Label><Input name="contact_person" value={form.contact_person || ''} onChange={handleChange} /></div>
                <div className="space-y-2"><Label>Email</Label><Input name="email" value={form.email || ''} onChange={handleChange} /></div>
                <div className="space-y-2"><Label>Phone</Label><Input name="phone" value={form.phone || ''} onChange={handleChange} /></div>
                <div className="space-y-2"><Label>Address</Label><Input name="address" value={form.address || ''} onChange={handleChange} /></div>
                <div className="space-y-2"><Label>City</Label><Input name="city" value={form.city || ''} onChange={handleChange} /></div>
                <div className="space-y-2"><Label>Province</Label><Input name="province" value={form.province || ''} onChange={handleChange} /></div>
                <div className="space-y-2"><Label>Postal Code</Label><Input name="postal_code" value={form.postal_code || ''} onChange={handleChange} /></div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-y-4">
                <InfoRow label="Contact" value={client.contact_person} />
                <InfoRow label="Email" value={client.email} />
                <InfoRow label="Phone" value={client.phone} />
                <InfoRow label="City" value={client.city} />
                <InfoRow label="Address" value={client.address} />
                <InfoRow label="Province" value={client.province} />
                <InfoRow label="Postal Code" value={client.postal_code} />
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium">Projects</CardTitle>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => navigate(`/projects?new=1&client_id=${clientId}`)} data-testid="add-project-from-client">
                <Plus className="w-4 h-4 mr-1" /> New
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {projects.length === 0 ? (
              <p className="text-sm text-slate-500">No projects yet</p>
            ) : (
              <div className="space-y-2">
                {projects.map(p => (
                  <Link key={p.id} to={`/projects/${p.id}`} className="block p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors duration-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{p.name}</p>
                        <p className="text-xs text-slate-500">{formatDate(p.created_at)}</p>
                      </div>
                      <Badge className={`text-xs ${p.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {p.status}
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <p className="text-xs text-slate-500 font-medium">{label}</p>
      <p className="text-sm text-slate-900">{value || '-'}</p>
    </div>
  );
}
